import {
	type ControlCharsType,
	type Message,
	type SDK_Filter,
	type SDK_Fonts,
	type SDK_Stack,
	type TextDecorations,
	type allModifiers,
	type keywords,
	strings,
} from "@quickpoint.me/shared";

import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import type { controlsHelp } from "../../displayComponents/sidebar/Hints";
import { postMessageToViewer } from "../messageBus";
import type { AppVars } from "./recoilStatesForCodeMirror";

/**
 * Ask the viewer to go to a page
 * @export
 */
export function goToPage(
	hostId: string,
	type: Message["type"],
	pageIdx: number,
): void {
	postMessageToViewer({
		hostId,
		type,
		action: strings.messages.goToPage,
		payload: pageIdx,
	});
}

// EVENTS

export function fireClearCodeEvent() {
	const evt = new CustomEvent(strings.events.clearCode, { bubbles: true });
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

/** Signal start of AI streaming - clears editor and sets aria-busy */
export function fireAiStreamStartEvent() {
	const evt = new CustomEvent(strings.events.aiStreamStart, { bubbles: true });
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

/** Stream a chunk of text character by character */
export function fireAiStreamChunkEvent(chunk: string) {
	const evt = new CustomEvent(strings.events.aiStreamChunk, {
		bubbles: true,
		detail: { chunk },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

/** Signal end of AI streaming - clears aria-busy */
export function fireAiStreamEndEvent() {
	const evt = new CustomEvent(strings.events.aiStreamEnd, { bubbles: true });
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireFoldEvent(codeIsFolded: boolean) {
	const evt = new CustomEvent(strings.events.fold, {
		bubbles: true,
		detail: { codeIsFolded },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireInsertWordEvent({
	word,
	insertAtLineNumber,
	newLine,
	isModifier,
}: {
	word:
		| (typeof keywords)[number]
		| (typeof allModifiers)[number]
		| ControlCharsType
		| keyof typeof controlsHelp;
	insertAtLineNumber: number | false;
	newLine?: boolean;
	// set when the caller knows (e.g. localized tokens, where the word can't
	// be looked up in modifierObject); insertWordAt infers it otherwise
	isModifier?: boolean;
}) {
	const evt = new CustomEvent(strings.events.insertWord, {
		bubbles: true,
		detail: { word, newLine, insertAtLineNumber, isModifier },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireInsertCharacterEvent(
	character: string,
	openAutoComplete = false,
) {
	const evt = new CustomEvent(strings.events.insertCharacter, {
		bubbles: true,
		detail: { character, openAutoComplete },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireMoveCursortoPageEvent(pageIdx: number) {
	const evt = new CustomEvent(strings.events.moveCursorToPageIndex, {
		bubbles: true,
		detail: { pageIdx },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateAppVarsEvent({ stackId, stackCfKey }: AppVars) {
	// console.log("fireUpdateAppVarsEvent", stackId, stackCfKey);
	const evt = new CustomEvent(strings.events.updateAppVars, {
		bubbles: true,
		detail: { appVars: { stackId, stackCfKey } },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateBackLinksEvent(backLinks: string[]) {
	const evt = new CustomEvent(strings.events.updateBacklinks, {
		bubbles: true,
		detail: { backLinks },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

// isLoad marks stack-load/display updates: they must not register as user
// input, or the loaded code gets written back into whatever stack atom the
// editor is currently keyed to (poisoning it during stack switches).
// Injections (AI, element editor) omit it so they persist like typed input.
export function fireUpdateCodeEvent(code: SDK_Stack["code"], isLoad = false) {
	const evt = new CustomEvent(strings.events.updateCode, {
		bubbles: true,
		detail: { code, isLoad },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateLoadedFilterFontsEvent(loadedFonts: SDK_Fonts) {
	const evt = new CustomEvent(strings.events.updateLoadedFilterFonts, {
		bubbles: true,
		detail: { loadedFonts },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateFilterColorsEvent(
	filterColors: SDK_Filter["colors"],
) {
	const evt = new CustomEvent(strings.events.updateFilterColors, {
		bubbles: true,
		detail: { filterColors },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateAudioFilesEvent(allAudioFiles: QpFile[]) {
	const evt = new CustomEvent(strings.events.updateAudioFiles, {
		bubbles: true,
		detail: { allAudioFiles },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateImageFilesEvent(allImageFiles: QpFile[]) {
	const evt = new CustomEvent(strings.events.updateImageFiles, {
		bubbles: true,
		detail: { allImageFiles },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateDataFilesEvent(allDataFiles: QpFile[]) {
	const evt = new CustomEvent(strings.events.updateDataFiles, {
		bubbles: true,
		detail: { allDataFiles },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUpdateFilterNamesEvent(filterNames: string[]) {
	const evt = new CustomEvent(strings.events.updateFilterNames, {
		bubbles: true,
		detail: { filterNames },
	});
	if (document.getElementsByClassName("cm-content")[0]) {
		document.getElementsByClassName("cm-content")[0].dispatchEvent(evt);
	}
}

export function fireUpdateStackCfKeys(stackCfKeys: string[]) {
	const evt = new CustomEvent(strings.events.updateStackCfKeys, {
		bubbles: true,
		detail: stackCfKeys,
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireWrapSelectionEvent(
	textModifier: (typeof TextDecorations)["underline" | "italic" | "bold"],
) {
	const evt = new CustomEvent(strings.events.wrapSelection, {
		bubbles: true,
		detail: { textModifier },
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireUndoEvent() {
	const evt = new CustomEvent(strings.events.undo, { bubbles: true });
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}

export function fireOpenAutoCompleteEvent() {
	const evt = new CustomEvent(strings.events.openAutoComplete, {
		bubbles: true,
	});
	const target = document.getElementsByClassName("cm-content")[0];
	if (target) target.dispatchEvent(evt);
}
